import dynamic from 'next/dynamic';
import { useMemo } from 'react';

import PageHeader from '../blocks/page-header/PageHeader';
import Timeline from '../blocks/timeline/Timeline';
import CardCarousel from '../blocks/card-carousel/CardCarousel';
import { isPostCarousel } from '../blocks/card-carousel/CardCarousel.transform.wp';
import CardGrid from '../blocks/card-grid/CardGrid';
import Heading from '../blocks/heading/Heading';
import Hero from '../blocks/hero/Hero';
import HeroCopy from '../blocks/hero-copy/HeroCopy';
import HeroCta from '../blocks/hero-cta/HeroCta';
import HeroNews from '../blocks/hero-news/HeroNews';
import NewsArticle from '../blocks/news-article/NewsArticle';
import Credits from '../blocks/credits/Credits';
import ImageParallax from '../blocks/image-parallax/ImageParallax';
import TextInlineImages from '../blocks/text-inline-images/TextInlineImages';
import Wysiwyg from '../blocks/wysiwyg/Wysiwyg';
import HeroHome from '../blocks/hero-home/HeroHome';
import GravityForm from '../blocks/gravity-form/GravityForm';
import IframeEmbed from '../blocks/iframe-embed/IframeEmbed';
import CallOuts from '../blocks/call-outs/CallOuts';

import CardDemo from '../blocks/card-demo/CardDemo';
import SectionHeading from '../blocks/section-heading/SectionHeading';

import Accordion from '../blocks/accordion/Accordion';
import EventDetails from '../blocks/event-details/EventDetails';
import ContentTwoImage from '../blocks/content-two-image/ContentTwoImage';
import ContentMediaHigh from '../blocks/content-media-high/ContentMediaHigh';

import ComponentInfo from '../fragments/component-info/ComponentInfo';

import NavigationFooter from '../blocks/navigation-footer/NavigationFooter';

// Below-the-fold / Swiper / video / SplitText blocks are code-split so their
// dependencies (swiper, hls.js, gsap) only ship on pages that use them.
// SSR stays on (next/dynamic default) so content/SEO is unaffected.
const Media = dynamic(() => import('../blocks/media/Media'));
const ImageCarousel = dynamic(
  () => import('../blocks/image-carousel/ImageCarousel')
);
const Quote = dynamic(() => import('../blocks/quote/Quote'));
const Video = dynamic(() => import('../blocks/video/Video'));
const GiveButterEmbed = dynamic(
  () => import('../blocks/givebutter-embed/GiveButterEmbed')
);
const ContentMedia = dynamic(
  () => import('../blocks/content-media/ContentMedia')
);
// Demo-only, but BLOCK_COMPONENTS is imported by the real catch-all route too —
// keep the style sheet out of every page's bundle.
const Typography = dynamic(() => import('../blocks/typography/Typography'));
const FeaturedStaff = dynamic(
  () => import('../blocks/featured-staff/FeaturedStaff')
);

export const BLOCK_COMPONENTS = {
  'image-parallax': ImageParallax,
  'text-inline-images': TextInlineImages,
  credits: Credits,
  'page-header': PageHeader,
  'hero-cta': HeroCta,
  'hero-copy': HeroCopy,
  'hero-news': HeroNews,
  timeline: Timeline,
  'card-carousel': CardCarousel,
  'card-grid': CardGrid,
  'call-outs': CallOuts,
  heading: Heading,
  media: Media,
  'image-carousel': ImageCarousel,
  quote: Quote,
  video: Video,
  wysiwyg: Wysiwyg,
  'hero-home': HeroHome,
  'gravity-form': GravityForm,
  'givebutter-embed': GiveButterEmbed,
  hero: Hero,
  accordion: Accordion,
  'card-demo': CardDemo,
  'section-heading': SectionHeading,
  'iframe-embed': IframeEmbed,
  'content-media': ContentMedia,
  'event-details': EventDetails,
  'content-two-image': ContentTwoImage,
  'content-media-high': ContentMediaHigh,
  typography: Typography,
  'featured-staff': FeaturedStaff,
};

// Components that need extra props
const getBlockProps = (
  block,
  index,
  { pageData, globalData, isInitialLoad },
  gravityForms
) => {
  const baseProps = { block, index };

  switch (block.type) {
    case 'gravity-form':
      return { ...baseProps, gravityForms };
    case 'page-header':
      return { ...baseProps, pageData };
    case 'hero-cta':
      return { ...baseProps, pageData, globalData };
    // HeroHome owns the first-load page loader and is the only block that
    // needs to know a hard load from a client-side navigation.
    case 'hero-home':
      return { ...baseProps, isInitialLoad };
    default:
      return baseProps;
  }
};

// Normalise blocks so no two consecutive blocks have colorBackground: true
const normaliseBlocks = (blocks) => {
  let prevColoured = false;
  let prevBlockType = null;

  return blocks.map((block) => {
    // News/event carousels are coloured by definition, so they keep their own
    // palette slot instead of being demoted to white by the rules below. They
    // still count as coloured for whatever follows them.
    if (isPostCarousel(block)) {
      prevBlockType = block.type;
      prevColoured = true;
      return block;
    }

    const isColoured = !!block.colorBackground;
    let result = block;
    let nextColoured = isColoured; // effective colour state passed to the next block

    if (prevBlockType === 'section-heading') {
      if (prevColoured) {
        result = { ...block, colorCopy: true };
        nextColoured = true; // <-- does colorCopy render a background? set accordingly
      } else {
        result = { ...block, theme: false };
        nextColoured = false;
      }
    } else if (isColoured && prevColoured) {
      if (block.type === 'call-outs') {
        result = { ...block, colorCopy: true };
        nextColoured = true; // <-- same decision point as above
      } else {
        result = { ...block, theme: false };
        nextColoured = false;
      }
    }

    prevBlockType = block.type;
    prevColoured = nextColoured;
    return result;
  });
};

// Render post type specific header
const PostTypeHeader = ({ postType, pageData, globalData }) => {
  // Stable object identities so these child blocks can be skipped by memoization
  // (React.memo/PureComponent) instead of always seeing a fresh block prop.
  const newsHeroBlock = useMemo(
    () =>
      postType === 'News' && pageData?.title
        ? {
            theme: 'dynamic',
            colorBackground: true,
            heading: pageData.title,
            description: pageData.newsData?.description,
            image: pageData.newsData?.image,
            category: pageData.newsData?.category,
          }
        : null,
    [postType, pageData]
  );

  const eventHeroBlock = useMemo(
    () =>
      postType === 'Event' && pageData?.title
        ? {
            theme: 'dynamic',
            colorBackground: true,
            heading: pageData.title,
            image: pageData.eventData?.mainImage,
            category:
              [pageData.eventData?.date, pageData.eventData?.time]
                .filter(Boolean)
                .join(' | ') || false,
          }
        : null,
    [postType, pageData]
  );

  const eventDetailsBlock = useMemo(
    () =>
      postType === 'Event'
        ? {
            theme: 'dynamic',
            heading: pageData.title,
            ...pageData.eventData,
            type: 'event-details',
          }
        : null,
    [postType, pageData]
  );

  const eventCtaBlock = useMemo(
    () =>
      postType === 'Event'
        ? {
            type: 'hero-cta',
            theme: 'dynamic',
            heading: 'Register for this event',
            description:
              "After registering, we'll send you a confirmation email with event details, directions and what to expect on the day.",
            image: pageData.eventData?.mainImage,
            imageBackground: pageData.eventData?.secondaryImage,
            cta: pageData.eventData?.cta,
          }
        : null,
    [postType, pageData]
  );

  if (postType === 'News') {
    return <>{newsHeroBlock && <HeroNews block={newsHeroBlock} />}</>;
  }

  if (postType === 'Event') {
    return (
      <>
        {eventHeroBlock && <HeroNews block={eventHeroBlock} postType="event" />}
        <EventDetails block={eventDetailsBlock} />
        <HeroCta block={eventCtaBlock} globalData={globalData} />
      </>
    );
  }

  return null;
};

export default function BlockLayout({
  blocks,
  pageData,
  gravityForms,
  isDemo = false,
  globalData,
  postType,
  isInitialLoad = false,
}) {
  const normalisedBlocks = useMemo(
    () => (blocks ? normaliseBlocks(blocks) : []),
    [blocks]
  );

  if (!blocks) return null;

  return (
    <div className="components">
      <PostTypeHeader
        postType={postType}
        pageData={pageData}
        globalData={globalData}
      />

      {postType === 'News' ? (
        <>
          <NewsArticle
            pageData={pageData}
            blocks={normalisedBlocks}
            BLOCK_COMPONENTS={BLOCK_COMPONENTS}
          />
          <CardCarousel
            block={{
              theme: 'dynamic',
              colorBackground: true,
              heading: 'Related stories',
              sourcePostType: 'news',
              filter: 'recent',
              customTemplate: 'default',
              source: 'postType',
              cardType: 'news',
            }}
          />
        </>
      ) : (
        <>
          {normalisedBlocks.map((block, i) => {
            const Component = BLOCK_COMPONENTS[block.type];

            if (!Component) return null;

            const key = block.blockId || `${block.type}-${i}`;
            const props = getBlockProps(
              block,
              i,
              // The component library renders every block on one page, so it
              // must never trigger HeroHome's first-load loader.
              { pageData, globalData, isInitialLoad: isInitialLoad && !isDemo },
              gravityForms
            );

            if (isDemo) {
              return (
                <div key={key}>
                  <ComponentInfo block={block} />
                  <Component {...props} />
                </div>
              );
            }

            return <Component {...props} key={key} />;
          })}
        </>
      )}

      {globalData && (
        <NavigationFooter
          block={globalData.navigationFooter}
          gravityForms={gravityForms}
        />
      )}
    </div>
  );
}
